import { SelectProps } from "antd";
import { useCascadeSelectOptions } from "components/UI/Selects/CascadeSelectSearchable/useCascadeSelectOptions.ts";
import { useCommissionGroupSelectOptions } from "components/UI/Selects/CommissionGroupSelectSearchable/useCommissionGroupSelectOptions.ts";
import { useConversionTypeSelectOptions } from "components/UI/Selects/ConversionTypeSelectSearchable/useConversionTypeSelectOptions.ts";
import { useIntegrationSelectOptions } from "components/UI/Selects/IntegrationSelectSearchable/useIntegrationSelectOptions.ts";
import { useUserSelectOptions } from "components/UI/Selects/UserSelectSearchable/useUserSelectOptions.ts";
import { WithSelect } from "components/UI/Table/UI/InteractiveCell/components/WithSelect.tsx";
import { AlwaysEditingFieldProps, FieldProps } from "components/UI/Table/UI/InteractiveCell/miscs/types.ts";
import { FieldValues } from "react-hook-form";

type CommonProps<T extends FieldValues> = Omit<SelectProps, "options" | "loading" | "showSearch"> &
    FieldProps<T> &
    AlwaysEditingFieldProps;

const WithCascadeSearchable = <T extends FieldValues = FieldValues>(props: CommonProps<T> & { offerId?: string }) => {
    const { offerId, ...restProps } = props;
    const { formControl, ...selectProps } = restProps;

    const { options, loading } = useCascadeSelectOptions({ offerId });

    return (
        <WithSelect formControl={formControl} options={options} loading={loading} showSearch {...selectProps} />
    );
};

const WithConversionTypeSearchable = <T extends FieldValues = FieldValues>(props: CommonProps<T>) => {
    const { formControl, ...selectProps } = props;

    const { options, loading } = useConversionTypeSelectOptions();

    return (
        <WithSelect
            formControl={formControl}
            options={options}
            loading={loading}
            showSearch
            {...selectProps}
        />
    );
};

const WithIntegrationSearchable = <T extends FieldValues = FieldValues>(props: CommonProps<T>) => {
    const { formControl, ...selectProps } = props;

    const { options, loading } = useIntegrationSelectOptions();

    return (
        <WithSelect
            formControl={formControl}
            options={options}
            loading={loading}
            showSearch
            {...selectProps}
        />
    );
};

const WithUserSearchable = <T extends FieldValues = FieldValues>(props: CommonProps<T> & { roleId?: string }) => {
    const { roleId, ...restProps } = props;
    const { formControl, ...selectProps } = restProps;

    const { options, loading } = useUserSelectOptions({ roleId });

    return <WithSelect formControl={formControl} options={options} loading={loading} showSearch {...selectProps} />;
};

const WithCommissionGroupSelect = <T extends FieldValues = FieldValues>(props: CommonProps<T>) => {
    const { formControl, ...selectProps } = props;

    const { options, loading } = useCommissionGroupSelectOptions();

    return <WithSelect formControl={formControl} options={options} loading={loading} showSearch {...selectProps} />;
};

export const WithSearchableSelect = {
    Cascade: WithCascadeSearchable,
    ConversionType: WithConversionTypeSearchable,
    Integration: WithIntegrationSearchable,
    User: WithUserSearchable,
    CommissionGroup: WithCommissionGroupSelect,
};
